<template>
  <v-container>
    <div style="display: flex; flex-direction: column; align-items: center; margin-bottom: 16px; position: relative;">
      <div style="display: flex; align-items: center; gap: 8px; justify-content: center; margin-bottom: 16px;">
        <v-btn color="primary" :to="{ name: 'TournamentView' }">
          {{ hasActiveTournament ? 'Back to Tournament' : 'Back to Home' }}
        </v-btn>
        <v-btn color="primary" @click="saveSettings">Save</v-btn>
        <v-btn color="error" :disabled="!structureName || structureOptions.length <= 1" @click="dialog = true" style="min-width: 40px;">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>

      <!-- Feedback -->
      <div v-if="feedback" style="position: absolute; top: 50px; margin-top: 16px; padding: 12px; border-radius: 4px; text-align: center; max-width: 500px; z-index: 1;"
           :style="{
             backgroundColor: feedback.includes('Error') ? '#ffebee' : '#e8f5e8',
             color: feedback.includes('Error') ? '#c62828' : '#2e7d32',
             border: feedback.includes('Error') ? '1px solid #ffcdd2' : '1px solid #c8e6c9'
           }">
        {{ feedback }}
      </div>

      <!-- Structure name / selector -->
      <div style="display: flex; align-items: center; gap: 8px; max-width: 600px; min-width: 300px; width: 100%; margin-bottom: 16px;">
        <v-combobox
          v-model="structureName"
          :items="structureOptions.map(o => o.name)"
          label="Tournament Structure"
          @update:model-value="onStructureNameChange"
          clearable
          style="flex: 1;"
        />
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Delete Tournament Structure?</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ structureName }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Overwrite Confirmation Dialog -->
    <v-dialog v-model="showOverwriteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">Overwrite Structure?</v-card-title>
        <v-card-text>
          A structure named <strong>"{{ structureName }}"</strong> already exists. Do you want to overwrite it?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showOverwriteDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="confirmOverwrite">Overwrite</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <h1>Settings</h1>

    <StructureEditorForm
      :structure="currentStructure"
      :isAdmin="userStore.admin"
      :showAnnouncement="hasActiveTournament"
      @change="handleFormChange"
    />
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useUserStore } from '@/stores/users';
import { useTournamentStore } from '@/stores/tournament';
import { db } from '@/firebaseConfig';
import { doc, getDoc, collection, addDoc, getDocs, query, where, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import {
  TOURNAMENT_SETTINGS_DB,
  TOURNAMENT_PLAYERS_DB,
  TOURNAMENT_STRUCTURES_DB
} from '@/constants/constants';
import { usePayouts } from '@/compostables/usePayouts';
import StructureEditorForm from '@/components/custom/StructureEditorForm.vue';

const userStore = useUserStore();
const tournamentStore = useTournamentStore();
const user = computed(() => userStore.user);
const hasActiveTournament = computed(() => !!tournamentStore.tournamentName);

const feedback = ref('');
const loading = ref(false);
const structureName = ref('');
const selectedStructureId = ref(null);
const structureOptions = ref([]);
const dialog = ref(false);
const showOverwriteDialog = ref(false);
const autoSaveTimeout = ref(null);

// The structure currently loaded into the editor
const currentStructure = ref(null);
// Latest form data emitted by StructureEditorForm (kept in sync via @change)
const currentFormData = ref(null);

const { recalculateAndSavePayouts } = usePayouts();

// ── form data sync ───────────────────────────────────────────────────────────

function handleFormChange(data) {
  currentFormData.value = data;
  if (tournamentStore.tournamentName) {
    autoSaveTournamentSettings();
  }
}

// ── auto-save ────────────────────────────────────────────────────────────────

async function autoSaveTournamentSettings() {
  if (autoSaveTimeout.value) clearTimeout(autoSaveTimeout.value);
  autoSaveTimeout.value = setTimeout(async () => {
    const activeTournamentName = tournamentStore.tournamentName;
    if (!activeTournamentName || !db || !currentFormData.value) return;
    const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, activeTournamentName);
    try {
      const fd = currentFormData.value;
      await updateDoc(settingsDoc, {
        defaultDuration:    fd.defaultDuration,
        breakDuration:      fd.breakDuration,
        levels:             fd.levels,
        buyinAmount:        fd.buyinAmount,
        rebuyAmount:        fd.rebuyAmount,
        addonAmount:        fd.addonAmount,
        rebuyAllowed:       fd.rebuyAllowed,
        rebuyEndLevel:      fd.rebuyEndLevel,
        addonAllowed:       fd.addonAllowed,
        buyinChips:         fd.buyinChips,
        addonChips:         fd.addonChips,
        maxTables:          fd.maxTables,
        maxPlayersPerTable: fd.maxPlayersPerTable,
        trackPlayersByName:        fd.trackPlayersByName,
        chipDenominations:         fd.chipDenominations,
        payoutTables:              fd.payoutTables,
        startCountdownSeconds:     fd.startCountdownSeconds     ?? 0,
        betweenRoundsPauseSeconds: fd.betweenRoundsPauseSeconds ?? 0,
        buyinRake:                 fd.buyinRake                 ?? 0,
        rebuyRake:                 fd.rebuyRake                 ?? 0,
        addonRake:                 fd.addonRake                 ?? 0,
        displayConfig:             fd.displayConfig,
        announcement:              { text: fd.announcement || '', updatedAt: serverTimestamp() },
      });
      feedback.value = 'Auto-saved tournament settings.';
      const playersDoc = doc(db, TOURNAMENT_PLAYERS_DB, activeTournamentName);
      const playersSnap = await getDoc(playersDoc);
      const playersArr = playersSnap.exists() ? (playersSnap.data().players || []) : [];
      await recalculateAndSavePayouts({
        userId:       user.value,
        tournamentId: activeTournamentName,
        players:      playersArr,
        buyinAmount:  fd.buyinAmount,
        rebuyAmount:  fd.rebuyAmount,
        addonAmount:  fd.addonAmount,
        rebuyAllowed: fd.rebuyAllowed,
        rebuyEndLevel: fd.rebuyEndLevel,
        addonAllowed: fd.addonAllowed,
        payoutTables: fd.payoutTables,
        buyinRake:    fd.buyinRake    ?? 0,
        rebuyRake:    fd.rebuyRake    ?? 0,
        addonRake:    fd.addonRake    ?? 0,
      });
    } catch (e) {
      console.error('Error auto-saving tournament settings:', e);
      feedback.value = 'Error auto-saving settings.';
    }
  }, 1500);
}

// ── load ─────────────────────────────────────────────────────────────────────

async function loadSettings() {
  if (!user.value) {
    feedback.value = 'No user logged in. Cannot load settings.';
    return;
  }
  if (!db) {
    feedback.value = 'Firestore not initialized.';
    return;
  }

  const activeTournamentName = tournamentStore.tournamentName;
  if (!activeTournamentName) {
    await loadStructureOptions();
    return;
  }

  const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, activeTournamentName);
  try {
    loading.value = true;
    const docSnap = await getDoc(settingsDoc);
    if (docSnap.exists()) {
      const d = docSnap.data();
      const loaded = {
        defaultDuration:    typeof d.defaultDuration === 'number'      ? d.defaultDuration    : 12,
        breakDuration:      typeof d.breakDuration === 'number'        ? d.breakDuration      : 5,
        levels:             Array.isArray(d.levels) && d.levels.length ? d.levels             : null,
        buyinAmount:        typeof d.buyinAmount === 'number'          ? d.buyinAmount        : 0,
        rebuyAmount:        typeof d.rebuyAmount === 'number'          ? d.rebuyAmount        : 0,
        addonAmount:        typeof d.addonAmount === 'number'          ? d.addonAmount        : 0,
        rebuyAllowed:       typeof d.rebuyAllowed === 'boolean'        ? d.rebuyAllowed       : !!d.rebuyAmount,
        rebuyEndLevel:      typeof d.rebuyEndLevel === 'number'        ? d.rebuyEndLevel      : null,
        addonAllowed:       typeof d.addonAllowed === 'boolean'        ? d.addonAllowed       : !!d.addonAmount,
        buyinChips:         typeof d.buyinChips === 'number'           ? d.buyinChips         : 1000,
        addonChips:         typeof d.addonChips === 'number'           ? d.addonChips         : 1000,
        maxTables:          typeof d.maxTables === 'number'            ? d.maxTables          : 0,
        maxPlayersPerTable: typeof d.maxPlayersPerTable === 'number'   ? d.maxPlayersPerTable : 10,
        trackPlayersByName:        typeof d.trackPlayersByName === 'boolean'        ? d.trackPlayersByName        : false,
        isGlobal:                  typeof d.isGlobal === 'boolean'                  ? d.isGlobal                  : false,
        startCountdownSeconds:     typeof d.startCountdownSeconds === 'number'     ? d.startCountdownSeconds     : 0,
        betweenRoundsPauseSeconds: typeof d.betweenRoundsPauseSeconds === 'number' ? d.betweenRoundsPauseSeconds : 0,
        buyinRake:                 typeof d.buyinRake === 'number'                 ? d.buyinRake                 : 0,
        rebuyRake:                 typeof d.rebuyRake === 'number'                 ? d.rebuyRake                 : 0,
        addonRake:                 typeof d.addonRake === 'number'                 ? d.addonRake                 : 0,
        isTemplate:         false,
        chipDenominations:  Array.isArray(d.chipDenominations) && d.chipDenominations.length ? d.chipDenominations : null,
        payoutTables:       Array.isArray(d.payoutTables) && d.payoutTables.length
          ? d.payoutTables.map(t => ({ ...t, usePercent: t.usePercent !== undefined ? t.usePercent : true }))
          : null,
        displayConfig:      d.displayConfig || null,
        announcement:       typeof d.announcement?.text === 'string' ? d.announcement.text : '',
      };
      structureName.value = typeof d.structureName === 'string' ? d.structureName : '';
      currentStructure.value = loaded;
      currentFormData.value = { ...loaded };
      feedback.value = 'Settings loaded.';
    } else {
      feedback.value = 'No settings found in Firestore.';
    }
    loading.value = false;
  } catch (e) {
    console.error('Error loading settings from Firestore:', e);
    feedback.value = 'Error loading settings.';
    loading.value = false;
  }
  await loadStructureOptions();
  // Resolve selectedStructureId from options
  if (structureName.value) {
    const matched = structureOptions.value.find(s => s.name === structureName.value);
    if (matched) selectedStructureId.value = matched.id;
  }
}

// ── save ─────────────────────────────────────────────────────────────────────

async function saveSettings() {
  if (!user.value || !structureName.value) {
    feedback.value = 'Please enter a structure name.';
    return;
  }
  if (!db) {
    feedback.value = 'Firestore not initialized.';
    return;
  }

  const existingStructure = structureOptions.value.find(s => s.name === structureName.value);

  if (existingStructure && existingStructure.isGlobal && existingStructure.user?.user_id !== user.value?.user_id) {
    feedback.value = 'Error: This is a global structure created by another user. Please save with a new name.';
    return;
  }

  if (existingStructure && existingStructure.user?.user_id === user.value?.user_id && selectedStructureId.value !== existingStructure.id) {
    showOverwriteDialog.value = true;
    return;
  }

  const activeTournamentName = tournamentStore.tournamentName;
  if (activeTournamentName) {
    const settingsDoc = doc(db, TOURNAMENT_SETTINGS_DB, activeTournamentName);
    try {
      loading.value = true;
      const fd = currentFormData.value || {};
      await updateDoc(settingsDoc, {
        defaultDuration:    fd.defaultDuration,
        breakDuration:      fd.breakDuration,
        levels:             fd.levels,
        structureName:      structureName.value,
        buyinAmount:        fd.buyinAmount,
        rebuyAmount:        fd.rebuyAmount,
        addonAmount:        fd.addonAmount,
        rebuyAllowed:       fd.rebuyAllowed,
        rebuyEndLevel:      fd.rebuyEndLevel,
        addonAllowed:       fd.addonAllowed,
        buyinChips:         fd.buyinChips,
        addonChips:         fd.addonChips,
        maxTables:          fd.maxTables,
        maxPlayersPerTable: fd.maxPlayersPerTable,
        trackPlayersByName:        fd.trackPlayersByName,
        chipDenominations:         fd.chipDenominations,
        payoutTables:              fd.payoutTables,
        isGlobal:                  fd.isGlobal,
        startCountdownSeconds:     fd.startCountdownSeconds     ?? 0,
        betweenRoundsPauseSeconds: fd.betweenRoundsPauseSeconds ?? 0,
        buyinRake:                 fd.buyinRake                 ?? 0,
        rebuyRake:                 fd.rebuyRake                 ?? 0,
        addonRake:                 fd.addonRake                 ?? 0,
        displayConfig:             fd.displayConfig || null,
        announcement:              { text: fd.announcement || '', updatedAt: serverTimestamp() },
      });
      feedback.value = 'Settings saved!';
      loading.value = false;
      const playersDoc = doc(db, TOURNAMENT_PLAYERS_DB, activeTournamentName);
      const playersSnap = await getDoc(playersDoc);
      const playersArr = playersSnap.exists() ? (playersSnap.data().players || []) : [];
      await recalculateAndSavePayouts({
        userId:       user.value,
        tournamentId: activeTournamentName,
        players:      playersArr,
        buyinAmount:  fd.buyinAmount,
        rebuyAmount:  fd.rebuyAmount,
        addonAmount:  fd.addonAmount,
        rebuyAllowed: fd.rebuyAllowed,
        addonAllowed: fd.addonAllowed,
        payoutTables: fd.payoutTables,
        buyinRake:    fd.buyinRake    ?? 0,
        rebuyRake:    fd.rebuyRake    ?? 0,
        addonRake:    fd.addonRake    ?? 0,
      });
    } catch (e) {
      console.error('Error saving settings to Firestore:', e);
      feedback.value = 'Error saving settings.';
      loading.value = false;
    }
  }
  await saveStructure();
  await loadStructureOptions();
}

async function confirmOverwrite() {
  showOverwriteDialog.value = false;
  await saveStructure();
  await loadStructureOptions();
}

async function saveStructure() {
  if (!structureName.value || !currentFormData.value) return;

  const fd = currentFormData.value;
  const structuresCol = collection(db, TOURNAMENT_STRUCTURES_DB);
  const q = query(structuresCol, where('user.user_id', '==', user.value?.user_id), where('name', '==', structureName.value));
  const querySnapshot = await getDocs(q);

  const existingStructure = structureOptions.value.find(s => s.id === selectedStructureId.value);
  let isTemplate = false;
  if (existingStructure && existingStructure.isTemplate) {
    if (structureName.value === existingStructure.name) {
      if (!userStore.admin) {
        feedback.value = 'Error: Cannot save template structures. Please change the name to create your own structure.';
        return;
      }
      isTemplate = fd.isTemplate;
    }
  } else {
    isTemplate = userStore.admin && fd.isTemplate;
  }

  const structureData = {
    name:               structureName.value,
    defaultDuration:    fd.defaultDuration,
    breakDuration:      fd.breakDuration,
    levels:             fd.levels,
    user:               user.value,
    buyinAmount:        fd.buyinAmount,
    rebuyAmount:        fd.rebuyAmount,
    addonAmount:        fd.addonAmount,
    rebuyAllowed:       fd.rebuyAllowed,
    rebuyEndLevel:      fd.rebuyEndLevel,
    addonAllowed:       fd.addonAllowed,
    buyinChips:         fd.buyinChips,
    addonChips:         fd.addonChips,
    maxTables:          fd.maxTables,
    maxPlayersPerTable: fd.maxPlayersPerTable,
    trackPlayersByName: fd.trackPlayersByName,
    chipDenominations:         fd.chipDenominations,
    payoutTables:              fd.payoutTables,
    isGlobal:                  fd.isGlobal,
    isTemplate,
    startCountdownSeconds:     fd.startCountdownSeconds     ?? 0,
    betweenRoundsPauseSeconds: fd.betweenRoundsPauseSeconds ?? 0,
    buyinRake:                 fd.buyinRake                 ?? 0,
    rebuyRake:                 fd.rebuyRake                 ?? 0,
    addonRake:                 fd.addonRake                 ?? 0,
    displayConfig:             fd.displayConfig || null,
  };

  if (!querySnapshot.empty) {
    await updateDoc(querySnapshot.docs[0].ref, structureData);
  } else {
    await addDoc(structuresCol, structureData);
  }
}

// ── load structure options ───────────────────────────────────────────────────

async function loadStructureOptions() {
  structureOptions.value = [];
  if (!userStore.user) return;
  const structuresCol = collection(db, TOURNAMENT_STRUCTURES_DB);
  const activeTournamentName = tournamentStore.tournamentName;
  const hasActiveTournament = !!activeTournamentName;

  const userStructuresQuery = query(structuresCol, where('user.user_id', '==', userStore.userid));
  const [userSnapshot] = await Promise.all([getDocs(userStructuresQuery)]);

  const allStructures = new Map();
  const processSnapshot = (snapshot) => {
    snapshot.forEach(docSnap => allStructures.set(docSnap.id, { id: docSnap.id, ...docSnap.data() }));
  };
  processSnapshot(userSnapshot);

  if (!hasActiveTournament) {
    const templateSnapshot = await getDocs(query(structuresCol, where('isTemplate', '==', true)));
    processSnapshot(templateSnapshot);
    if (userStore.admin) {
      const globalSnapshot = await getDocs(query(structuresCol, where('isGlobal', '==', true)));
      processSnapshot(globalSnapshot);
    }
  } else {
    const globalSnapshot = await getDocs(query(structuresCol, where('isGlobal', '==', true)));
    processSnapshot(globalSnapshot);
  }

  structureOptions.value = Array.from(allStructures.values());
}

// ── structure name selector ──────────────────────────────────────────────────

function onStructureNameChange() {
  const structure = structureOptions.value.find(s => s.name === structureName.value);
  if (structure) {
    selectedStructureId.value = structure.id;
    currentStructure.value = structure;
    currentFormData.value = { ...structure };
  } else {
    selectedStructureId.value = null;
  }
}

// ── delete ───────────────────────────────────────────────────────────────────

async function deleteStructure() {
  if (!structureName.value || structureOptions.value.length <= 1) return;
  try {
    const toDelete = structureOptions.value.find(s => s.name === structureName.value);
    if (!toDelete) return;
    await deleteDoc(doc(db, TOURNAMENT_STRUCTURES_DB, toDelete.id));
    structureOptions.value = structureOptions.value.filter(s => s.id !== toDelete.id);
    feedback.value = 'Tournament structure deleted.';
    if (structureOptions.value.length > 0) {
      const next = structureOptions.value[0];
      structureName.value = next.name;
      selectedStructureId.value = next.id;
      currentStructure.value = next;
      currentFormData.value = { ...next };
    } else {
      structureName.value = '';
      selectedStructureId.value = null;
      currentStructure.value = null;
      currentFormData.value = null;
    }
  } catch (e) {
    console.error('Error deleting structure:', e);
    feedback.value = 'Error deleting structure.';
  }
}

function confirmDelete() {
  deleteStructure();
  dialog.value = false;
}

onMounted(() => {
  loadSettings();
});
</script>

<style scoped>
</style>

<style>
.chip-visual {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.2rem;
  border: 8px dashed;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2), inset 0 2px 4px rgba(255,255,255,0.5);
  text-shadow: 0 1px 1px rgba(0,0,0,0.2);
}
</style>
